import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';
import React from 'react';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { PolicyContext, PolicyData } from '../../context/policy';
import {
	capitalize,
	formatCurrency,
	formatName,
	getPolicyStatus,
} from '../../util/util';
import { StyledSectionContainer } from '../common';
import DisplayCard from '../DisplayCard';
import { getCurrentGraceLapse } from '../search/util';
import { getTotalClaimReserves } from './util';

interface Props {
	claimData: ClaimData;
	authData: AuthData;
	policyData: PolicyData;
}

const ClaimsOverview = (props: Props) => {
	const { claimData, policyData, authData } = props;

	const { claim, subclaimSummaries } = claimData;
	const { policy, graceLapse } = policyData;
	const { totalLossReserve, totalExpenseReserve } =
		getTotalClaimReserves(subclaimSummaries);
	const reserveCurrency = policy.currency;
	const state = getPolicyStatus(policy, getCurrentGraceLapse(graceLapse));

	const renderDetailCard = (title: string, data: string, link?: string) => {
		return <DisplayCard title={title} value={data} link={link} />;
	};

	return (
		<StyledSectionContainer>
			<Stack spacing={2}>
				<Typography id='claim_id' variant='h3' gutterBottom>
					{`Claim ${claim.locator}`}
				</Typography>
				<Stack spacing={2} direction='row'>
					{renderDetailCard('Product', formatName(policy.productName))}
					{renderDetailCard(
						'Policy Created',
						authData.getFormattedDate(policy.createdTimestamp),
					)}
					{renderDetailCard(
						'Policy Last Modified',
						authData.getFormattedDate(policy.updatedTimestamp),
					)}
					{renderDetailCard('Policy State', state)}
				</Stack>
				<Stack spacing={2} direction='row'>
					{renderDetailCard(
						'Claim Created',
						authData.getFormattedDate(claim.createdTimestamp),
					)}
					{renderDetailCard(
						'Claim Last Modified',
						authData.getFormattedDate(claim.updatedTimestamp),
					)}
					{renderDetailCard('Claim State', capitalize(claim.currentStatus))}
					{renderDetailCard(
						'Total Loss Reserve',
						formatCurrency(Math.abs(totalLossReserve), reserveCurrency),
					)}
					{renderDetailCard(
						'Total Expense Reserve',
						formatCurrency(Math.abs(totalExpenseReserve), reserveCurrency),
					)}
				</Stack>
			</Stack>
		</StyledSectionContainer>
	);
};

function withContext<T>(Component: any) {
	return (props: Omit<T, 'claimData' | 'authData' | 'policyData'>) => (
		<ClaimContext.Consumer>
			{(claimData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<AuthContext.Consumer>
							{(authData) => (
								<Component
									{...props}
									claimData={claimData}
									policyData={policyData}
									authData={authData}
								/>
							)}
						</AuthContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</ClaimContext.Consumer>
	);
}

export default withContext(ClaimsOverview);
